import { useEffect, useState } from "react";

import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { ClientRiskData } from "~/pages/risk/components/ClientRiskTable";

export const useClientRisk = () => {
  const [pendingClients, setPendingClients] = useState<ClientRiskData[]>([]);
  const [processedClients, setProcessedClients] = useState<ClientRiskData[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState<ClientRiskData | null>(
    null
  );
  const [insuredAmount, setInsuredAmount] = useState<number>(0);
  const [trailingPaymentDays, setTrailingPaymentDays] = useState<number>(0);
  const [salesConditionId, setSalesConditionId] = useState<string>("");
  const [activeTab, setActiveTab] = useState("pending");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      // Fetch clients that have not been risk checked
      const { data: pendingData, error: pendingError } = await supabase
        .from("clients")
        .select(
          "id, name, tax_id, email, insured_amount, trailing_payment_days, sales_condition_id, created_at"
        )
        .eq("risk_checked", false);

      // Fetch clients that have been risk checked
      const { data: processedData, error: processedError } = await supabase
        .from("clients")
        .select(
          "id, name, tax_id, email, insured_amount, trailing_payment_days, sales_condition_id, created_at"
        )
        .eq("risk_checked", true);

      if (pendingError) throw pendingError;
      if (processedError) throw processedError;

      const mapClientData = (client: any): ClientRiskData => ({
        id: client.id,
        name: client.name,
        company: client.name, // Using name as company for now
        taxId: client.tax_id || "",
        email: client.email || "",
        insuredAmount: client.insured_amount || 0,
        trailingPaymentDays: client.trailing_payment_days || 0,
        salesConditionId: client.sales_condition_id || undefined,
        createdAt: client.created_at,
      });

      setPendingClients(pendingData.map(mapClientData));
      setProcessedClients(processedData.map(mapClientData));
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los clientes.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (client: ClientRiskData) => {
    setEditingClient(client);
    setInsuredAmount(client.insuredAmount);
    setTrailingPaymentDays(client.trailingPaymentDays);
    setSalesConditionId(client.salesConditionId || "");
  };

  const cancelEditing = () => {
    setEditingClient(null);
  };

  const saveClientRiskData = async () => {
    if (!editingClient) return;

    try {
      const { error } = await supabase
        .from("clients")
        .update({
          insured_amount: insuredAmount,
          trailing_payment_days: trailingPaymentDays,
          sales_condition_id: salesConditionId || null,
          risk_checked: true,
        })
        .eq("id", editingClient.id);

      if (error) throw error;

      // Update local state
      if (activeTab === "pending") {
        setPendingClients(
          pendingClients.filter((c) => c.id !== editingClient.id)
        );
      }

      // Update the processed clients list
      const updatedClient = {
        ...editingClient,
        insuredAmount,
        trailingPaymentDays,
        salesConditionId: salesConditionId || undefined,
      };

      // If this client was already in processed clients, update it; otherwise add it
      const clientInProcessed = processedClients.some(
        (c) => c.id === editingClient.id
      );

      if (clientInProcessed) {
        setProcessedClients(
          processedClients.map((client) =>
            client.id === editingClient.id ? updatedClient : client
          )
        );
      } else {
        setProcessedClients([updatedClient, ...processedClients]);
      }

      setEditingClient(null);

      toast({
        title: "Cliente Actualizado",
        description:
          "La información de riesgo del cliente ha sido actualizada con éxito.",
      });

      // Dispatch custom event to notify the client detail page to refresh
      window.dispatchEvent(
        new CustomEvent("client-updated", {
          detail: { clientId: editingClient.id },
        })
      );
    } catch (error) {
      console.error("Error updating client:", error);
      toast({
        title: "Error",
        description: "No se pudo actualizar la información del cliente.",
        variant: "destructive",
      });
    }
  };

  return {
    pendingClients,
    processedClients,
    loading,
    editingClient,
    insuredAmount,
    trailingPaymentDays,
    salesConditionId,
    activeTab,
    setActiveTab,
    setInsuredAmount,
    setTrailingPaymentDays,
    setSalesConditionId,
    startEditing,
    cancelEditing,
    saveClientRiskData,
  };
};
